import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sentinel } from '../library/Sentinel'
import { useMediaSrc } from '../library/useMediaSrc'
import { stripHtml, type Raw } from './chatRows'
import { useChatsDeps } from './ChatsDeps'
import { Icon } from './Icon'
import { last30Days, ofUtc } from './statsRange'

/**
 * The Chats page's mass-message stats (ADR 0024): the last 30 days of the
 * account's mass messages, read from OnlyFans' own statistics endpoints
 * through the CRM's allow-listed POST /v1/statistics/of (the same contract
 * the CRM Statistics page captured, docs/of_statistics_capture_2026-10-07).
 */

const PAGE_SIZE = 10

const money = (n: unknown) => {
  const v = Number(n)
  return Number.isFinite(v) && v > 0 ? `$${v.toFixed(2)}` : '$0.00'
}
const count = (n: unknown) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-US') : '—')

interface Point {
  date: string
  count: number
}

function points(series: unknown): Point[] {
  const chart = (series as { chart?: unknown } | undefined)?.chart
  return Array.isArray(chart) ? chart.map((p: Raw) => ({ date: String(p.date ?? ''), count: Number(p.count) || 0 })) : []
}

function Chart({ purchases, messages }: { purchases: Point[]; messages: Point[] }) {
  const line = (pts: Point[]) => {
    if (pts.length < 2) return ''
    const max = Math.max(1, ...pts.map((p) => p.count))
    return pts.map((p, i) => `${((i / (pts.length - 1)) * 500).toFixed(1)},${(130 - (p.count / max) * 110).toFixed(1)}`).join(' ')
  }
  return (
    <svg className="cs-chart" viewBox="0 0 500 140" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 30H500M0 60H500M0 90H500M0 130H500" className="cs-chart-grid" />
      {messages.length > 1 && <polyline points={line(messages)} className="cs-chart-messages" />}
      {purchases.length > 1 && <polyline points={line(purchases)} className="cs-chart-purchases" />}
    </svg>
  )
}

function Thumb({ media }: { media: unknown }) {
  const first = Array.isArray(media) ? (media[0] as Raw | undefined) : undefined
  const files = (first?.files ?? {}) as Raw
  const url = String((files.thumb as Raw | undefined)?.url ?? (files.preview as Raw | undefined)?.url ?? first?.thumb ?? '')
  const src = useMediaSrc(url)
  return (
    <span className="cs-thumb" ref={src.ref as React.RefObject<HTMLSpanElement>}>
      {src.src ? <img src={src.src} alt="" loading="lazy" /> : null}
    </span>
  )
}

function textOf(m: Raw): string {
  return String(m.rawText ?? '').trim() || stripHtml(m.text)
}

function dateOf(m: Raw, locale: string): string {
  const d = new Date(String(m.date ?? m.createdAt ?? ''))
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}, ${d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })} ${d.getFullYear()}`
}

export function StatsPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation('chats')
  const { local, now } = useChatsDeps()
  const range = useMemo(() => last30Days(now()), [now])
  const stats = useCallback((path: string, query: Raw) => local<Raw>('/v1/statistics/of', { path, query }), [local])

  const [chart, setChart] = useState<Raw | null>(null)
  const [summary, setSummary] = useState<Raw | null>(null)
  const [top, setTop] = useState<Raw | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    stats('messages/queue/chart', {
      ...range,
      withTotal: 'true',
      'filter[group_messages]': 'group_messages',
      'filter[group_messages_purchases]': 'group_messages_purchases',
    }).then(
      (d) => live && setChart(d ?? {}),
      (e: Error) => live && setError(e.message),
    )
    stats('users/me/stats/overview', { ...range, by: 'messages' }).then(
      (d) => live && setSummary(d ?? {}),
      () => live && setSummary({}),
    )
    stats('users/me/stats/top/message', range).then(
      (d) => live && setTop(d ?? {}),
      () => live && setTop({}),
    )
    return () => {
      live = false
    }
  }, [stats, range])

  // OnlyFans pages this list by moving endDate back to the last message's date.
  const [rows, setRows] = useState<Raw[]>([])
  const [cursor, setCursor] = useState<string>(range.endDate)
  const [hasMore, setHasMore] = useState(true)
  const loading = useRef(false)
  const loadMore = useCallback(async () => {
    if (loading.current || !hasMore) return
    loading.current = true
    try {
      const body = await stats('users/me/stats/messages/group', { startDate: range.startDate, endDate: cursor, limit: PAGE_SIZE })
      const items = Array.isArray(body?.items) ? (body.items as Raw[]) : []
      const last = items[items.length - 1]
      const lastDate = last ? new Date(String(last.date ?? last.createdAt ?? '')) : null
      setRows((prev) => {
        const seen = new Set(prev.map((r) => String(r.id)))
        return [...prev, ...items.filter((r) => !seen.has(String(r.id)))]
      })
      if (lastDate && Number.isFinite(lastDate.getTime())) setCursor(ofUtc(lastDate))
      setHasMore(Boolean(body?.hasMore) && Boolean(lastDate))
    } catch (e) {
      setError((e as Error).message)
      setHasMore(false)
    } finally {
      loading.current = false
    }
  }, [stats, range.startDate, cursor, hasMore])

  const [query, setQuery] = useState('')
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? rows.filter((r) => `${textOf(r)} ${dateOf(r, i18n.language)}`.toLowerCase().includes(q)) : rows
  }, [rows, query, i18n.language])

  const messages = chart?.group_messages as Raw | undefined
  const purchases = chart?.group_messages_purchases as Raw | undefined
  const mass = summary?.massMessages as Raw | undefined
  const topMessage = top?.purchases as Raw | undefined

  return (
    <div className="cs-screen">
      <section className="cs-main">
        <header className="cs-head">
          <button type="button" className="chats-panel-tool" aria-label={t('stats.back')} title={t('stats.back')} onClick={onClose}>
            <Icon id="icon-back" size={18} />
          </button>
          <h1>{t('stats.title')}</h1>
        </header>
        <div className="cs-period">
          <strong>{t('stats.period')}</strong>
          <span>{t('stats.utc')}</span>
        </div>
        {chart && (
          <>
            <div className="cs-count">
              {t('stats.count', { n: count(messages?.total), amount: money(purchases?.total) })}
            </div>
            <Chart purchases={points(purchases)} messages={points(messages)} />
          </>
        )}
        {error && <div className="cs-error">{error}</div>}
        <label className="cs-search">
          <Icon id="icon-search" size={16} />
          <input type="search" placeholder={t('stats.search')} aria-label={t('stats.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <table className="cs-table" aria-label={t('stats.table')}>
          <thead>
            <tr>
              <th>{t('stats.date')}</th>
              <th>{t('stats.message')}</th>
              <th className="num">{t('stats.sent')}</th>
              <th className="num">{t('stats.viewed')}</th>
              <th className="num">{t('stats.price')}</th>
              <th className="num">{t('stats.purchases')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) => (
              <tr key={String(m.id)}>
                <td className="cs-date">{dateOf(m, i18n.language)}</td>
                <td>
                  <span className="cs-message">
                    <Thumb media={m.media} />
                    <span className="cs-text">{textOf(m) || t('stats.noText')}</span>
                  </span>
                </td>
                <td className="num">{count(m.sentCount)}</td>
                <td className="num">{count(m.viewedCount)}</td>
                <td className="num">{Number(m.price) > 0 ? money(m.price) : '—'}</td>
                <td className="num">{Number(m.purchasedCount) > 0 ? count(m.purchasedCount) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && !hasMore && <div className="cs-empty">{t('stats.empty')}</div>}
        {hasMore && <Sentinel onVisible={loadMore} token={rows.length} />}
      </section>
      <aside className="cs-side">
        <div className="cs-card">
          <h3>{t('stats.summary')}</h3>
          {mass?.hasStatistic ? (
            <dl>
              <dt>{t('stats.messages')}</dt>
              <dd>{count((mass.count as Raw | undefined)?.total)}</dd>
              <dt>{t('stats.earnings')}</dt>
              <dd>{money((mass.earnings as Raw | undefined)?.total)}</dd>
            </dl>
          ) : (
            <p>{summary ? t('stats.empty') : '…'}</p>
          )}
        </div>
        <div className="cs-card">
          <h3>{t('stats.top')}</h3>
          <p>{topMessage ? textOf(topMessage) || t('stats.noText') : top ? t('stats.empty') : '…'}</p>
        </div>
      </aside>
    </div>
  )
}
